import { useCallback, useEffect, useMemo, useState } from "react";
import { PieChart, pieArcLabelClasses } from "@mui/x-charts/PieChart";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import DataTableBase from "../interfaccia/DataTableBase";
import apiUrl from "../Apiurl";
import stilePulsanti from "../interfaccia/stilePulsanti";
import { PUBLISH_STATUS_COLORS } from "./publicationStatus";

const DAYS_OPTIONS = [
  { value: 7, label: "Ultimi 7 giorni" },
  { value: 14, label: "Ultimi 14 giorni" },
  { value: 30, label: "Ultimi 30 giorni" },
];

const STOCK_FILTER_OPTIONS = [
  { value: "all", label: "Tutti gli articoli" },
  { value: "moda", label: "Articoli con stock Moda > 0" },
  {
    value: "web",
    label: "Articoli con stock web (Moda + fornitori) > 0",
  },
];

const PUBLISH_STATUS_ORDER = ["SI", "DA CONTROLLARE", "NO", "PROBLEMA", "NON PUBBLICARE"];
const IMAGE_STATUS_ORDER = ["SI", "NO", "NON SI TROVA"];
const IMAGE_STATUS_COLORS = {
  SI: "#2e7d32",
  NO: "#cfd8dc",
  "NON SI TROVA": "#ffa000",
};

function generateSeasonCodes(startYear, endYear) {
  const codes = [];
  for (let year = startYear; year <= endYear; year++) {
    const yearCode = String(year).slice(-2).padStart(2, "0");
    codes.push(`${yearCode}1`, `${yearCode}2`);
  }
  return codes;
}

const BRAND_COLOR_PALETTE = [
  "#1976d2",
  "#43a047",
  "#fb8c00",
  "#8e24aa",
  "#ef5350",
  "#00acc1",
  "#7cb342",
  "#6d4c41",
  "#c2185b",
  "#5c6bc0",
  "#f9a825",
  "#00897b",
  "#9c27b0",
  "#ff7043",
  "#26a69a",
  "#d4e157",
];

const DASHBOARD_TEXT_COLOR = "#1976d2";
const DASHBOARD_IMAGE_COLOR = "#fdd835";
const DASHBOARD_STOCK_COLOR = "#3949ab";
const USER_CHART_DEFAULT_HEIGHT = 360;

function getUserChartViewportHeight() {
  if (typeof window === "undefined") {
    return USER_CHART_DEFAULT_HEIGHT;
  }
  return Math.max(
    USER_CHART_DEFAULT_HEIGHT,
    Math.floor(window.innerHeight * 0.75)
  );
}

function normalizeBrandKey(value) {
  const normalized = (value ?? "").toString().trim();
  return normalized.length ? normalized.toUpperCase() : "NO_BRAND";
}

function getBrandLabel(code, description) {
  const label = (description ?? "").toString().trim();
  if (label.length) {
    return label;
  }
  const normalizedCode = (code ?? "").toString().trim();
  return normalizedCode.length ? normalizedCode : "Senza brand";
}

function computeBrandColor(key) {
  const normalized = normalizeBrandKey(key);
  let hash = 0;
  for (let i = 0; i < normalized.length; i += 1) {
    hash = (hash * 31 + normalized.charCodeAt(i)) >>> 0;
  }
  return BRAND_COLOR_PALETTE[hash % BRAND_COLOR_PALETTE.length];
}

function lightenColor(hex, amount = 0.35) {
  if (typeof hex !== "string") {
    return hex;
  }
  const normalized = hex.replace("#", "");
  if (![3, 6].includes(normalized.length)) {
    return hex;
  }
  const value =
    normalized.length === 3
      ? normalized
          .split("")
          .map((char) => char + char)
          .join("")
      : normalized;
  const num = Number.parseInt(value, 16);
  if (Number.isNaN(num)) {
    return hex;
  }
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  const adjust = (channel) =>
    Math.min(255, Math.round(channel + (255 - channel) * amount));
  return `#${adjust(r).toString(16).padStart(2, "0")}${adjust(g)
    .toString(16)
    .padStart(2, "0")}${adjust(b).toString(16).padStart(2, "0")}`;
}

function formatWeekLabel(weekRow) {
  if (!weekRow) return "-";
  const year = weekRow.year ?? weekRow.anno ?? weekRow.y ?? weekRow.yearWeek;
  const isoWeek = weekRow.isoWeek ?? weekRow.week ?? weekRow.settimana;
  if (!year || !isoWeek) {
    return "-";
  }
  const paddedWeek = String(isoWeek).padStart(2, "0");
  return `${year}-W${paddedWeek}`;
}

function getIsoWeekInfo(date) {
  const tmp = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  );
  const dayNumber = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNumber);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((tmp - yearStart) / 86400000 + 1) / 7);
  return { year: tmp.getUTCFullYear(), isoWeek: weekNo };
}

function parseWeekStart(weekRow) {
  if (!weekRow) return null;
  const raw =
    weekRow.weekStart ||
    weekRow.week_start ||
    weekRow.weekStartDate ||
    weekRow.week_start_date;
  if (raw) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) {
      return d;
    }
  }
  const year = Number(weekRow.year ?? weekRow.anno ?? 0);
  const isoWeek = Number(
    weekRow.isoWeek ?? weekRow.week ?? weekRow.settimana ?? 0
  );
  if (!year || !isoWeek) return null;
  // ISO week start helper (UTC)
  const simple = new Date(Date.UTC(year, 0, 1 + (isoWeek - 1) * 7));
  const dow = simple.getUTCDay() || 7;
  const monday = new Date(simple);
  monday.setUTCDate(simple.getUTCDate() - dow + 1);
  return monday;
}

function formatNumber(value) {
  if (typeof value !== "number") {
    return "-";
  }
  return value.toLocaleString("it-IT");
}

function formatValueTooltip(value) {
  if (value === null || typeof value === "undefined") {
    return "-";
  }
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return "-";
  }
  return num.toLocaleString("it-IT");
}

function splitImportedValues(totalValue, importedValue = 0) {
  const total = Math.max(Number(totalValue ?? 0), 0);
  const imported = Math.min(Math.max(Number(importedValue ?? 0), 0), total);
  const fresh = Math.max(total - imported, 0);
  return { total, imported, fresh };
}

function formatCountWithImport(total, imported) {
  const values = splitImportedValues(total, imported);
  return (
    <span>
      {formatNumber(values.total)}{" "}
      <span className="scrittaColAltScuro">
        ({formatNumber(values.imported)})
      </span>
    </span>
  );
}

function BrandList({ items = [], limit = 6 }) {
  if (!items.length) {
    return <span className="gray">-</span>;
  }
  const visibleItems = items.slice(0, limit);
  const remaining = items.length - visibleItems.length;

  return (
    <div className="flex flex-wrap mb2 mt2">
      {visibleItems.map((brand) => (
        <span
          key={`${brand.brandCode || "no-brand"}-${
            brand.brandDescription || ""
          }`}
          className="ba b--light-gray br-pill ph2 pv1 f7 mh1 mv1 bg-white"
        >
          <span className="b mr1">
            {brand.brandDescription || brand.brandCode}
          </span>
          <span className="gray">({brand.count})</span>
        </span>
      ))}
      {remaining > 0 && (
        <span className="ba b--light-gray br-pill ph2 pv1 f7 mh1 mv1 bg-white">
          +{remaining} altri
        </span>
      )}
    </div>
  );
}

//TODO inserire in tabella un link per aprire pagina "pubblicazioni" con i filtri preimpostati per vedere i prodotti assegnati all'utente relativo (l'utente in riga)

function DashboardPubblicazioni({ token, temaColori = "rootTema" }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refreshIndex, setRefreshIndex] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [showBrandStacks, setShowBrandStacks] = useState(false);
  const [selectedBrandSeason, setSelectedBrandSeason] = useState("");
  const [selectedBrand, setSelectedBrand] = useState(null); // Changed from selectedBrandSeason to selectedBrand (object or string)
  const [hiddenSeries, setHiddenSeries] = useState({});
  const [historyYears, setHistoryYears] = useState(1);
  const [minSeasonYear, setMinSeasonYear] = useState(
    new Date().getFullYear() - 1
  );
  const [stockMode, setStockMode] = useState("web");
  const [userChartHeight, setUserChartHeight] = useState(
    getUserChartViewportHeight
  );

  const italyDateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat("it-IT", {
        timeZone: "Europe/Rome",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
    []
  );

  const formatItalianDate = useCallback(
    (value) => {
      if (!value) {
        return "-";
      }
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) {
        return "-";
      }
      return italyDateFormatter.format(parsed);
    },
    [italyDateFormatter]
  );

  useEffect(() => {
    const handleResize = () => {
      setUserChartHeight(getUserChartViewportHeight());
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!token) {
      return;
    }

    const controller = new AbortController();

    async function fetchData() {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(
          `${apiUrl}/moda/publishing/dashboard?days=${days}&history_years=${historyYears}&stock_mode=${stockMode}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
          }
        );
        if (!response.ok) {
          const message = await response.text();
          throw new Error(message || "Errore nel caricamento dei dati.");
        }
        const payload = await response.json();
        if (!payload.success) {
          throw new Error(payload.error || "Risposta non valida dal server.");
        }
        setData(payload.data);
        setUpdatedAt(new Date());
      } catch (err) {
        if (!controller.signal.aborted) {
          setError(err.message || "Errore imprevisto durante il caricamento.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    fetchData();

    return () => controller.abort();
  }, [token, days, refreshIndex, historyYears, stockMode]);

  const stockFilterLabel =
    stockMode === "web"
      ? "Stock web>0"
      : stockMode === "moda"
      ? "Stock Moda>0"
      : "";

  const seasonsOfInterest = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return generateSeasonCodes(minSeasonYear, currentYear + 1);
  }, [minSeasonYear]);

  const rangeInfo = useMemo(() => {
    if (!data?.range) {
      return null;
    }
    return {
      start: formatItalianDate(data.range.startUtc),
      end: formatItalianDate(data.range.endUtc),
      days: data.range.days,
    };
  }, [data, formatItalianDate]);

  const summaryCards = useMemo(() => {
    if (!data?.summary) {
      return [];
    }
    const summary = data.summary;
    return [
      { label: "Articoli attivi", value: summary.totalArticoli },
      { label: "Da pubblicare", value: summary.needsTexts },
      { label: "Immagini mancanti", value: summary.needsImages },
      { label: "Pubblicati nel periodo", value: summary.publishedInRange },
      { label: "Immagini completate", value: summary.imagesInRange },
    ];
  }, [data]);

  const seasonPieCharts = useMemo(() => {
    const rawStats =
      data?.seasonStats ??
      data?.seasonPublishingStats ??
      data?.publishingBySeason ??
      data?.seasonalPublishingStats ??
      null;
    if (!rawStats) {
      return [];
    }
    const statsArray = Array.isArray(rawStats)
      ? rawStats
      : Object.entries(rawStats).map(([seasonKey, value]) => {
          if (value && typeof value === "object" && !Array.isArray(value)) {
            return { season: seasonKey, ...value };
          }
          return { season: seasonKey, counts: value };
        });

    const statMap = new Map();
    statsArray.forEach((row) => {
      const seasonCode =
        row.season ??
        row.seasonCode ??
        row.stagione ??
        row.code ??
        row.id ??
        row.label;
      if (!seasonCode) {
        return;
      }
      const countsSource =
        (row.counts &&
          typeof row.counts === "object" &&
          !Array.isArray(row.counts) &&
          row.counts) ||
        (row.statuses &&
          typeof row.statuses === "object" &&
          !Array.isArray(row.statuses) &&
          row.statuses) ||
        (row.values &&
          typeof row.values === "object" &&
          !Array.isArray(row.values) &&
          row.values) ||
        row;

      const counts = {};
      let total = 0;
      PUBLISH_STATUS_ORDER.forEach((statusKey) => {
        const normalizedKey = statusKey.replace(/\s+/g, "_").toLowerCase();
        const value =
          Number(
            countsSource?.[statusKey] ??
              countsSource?.[statusKey.toLowerCase()] ??
              countsSource?.[normalizedKey] ??
              countsSource?.[normalizedKey.toUpperCase()]
          ) || 0;
        counts[statusKey] = value;
        total += value;
      });

      if (total > 0) {
        statMap.set(String(seasonCode), {
          seasonCode: String(seasonCode),
          counts,
          total,
        });
      }
    });

    if (!statMap.size) {
      return [];
    }

    const chartsSource = seasonsOfInterest
      .map((seasonCode) => statMap.get(seasonCode))
      .filter(Boolean);

    const orderedCharts =
      chartsSource.length > 0
        ? chartsSource
        : Array.from(statMap.values()).sort((a, b) => b.total - a.total);

    return orderedCharts.map((entry) => ({
      ...entry,
      slices: PUBLISH_STATUS_ORDER.filter(
        (statusKey) => (entry.counts[statusKey] ?? 0) > 0
      ).map((statusKey) => ({
        id: statusKey,
        label: statusKey,
        value: entry.counts[statusKey],
        color: PUBLISH_STATUS_COLORS[statusKey],
      })),
    }));
  }, [data, seasonsOfInterest]);

  const seasonBrandStats = data?.seasonBrandStats ?? [];

  const brandSeasonOptions = useMemo(() => {
    if (!Array.isArray(seasonBrandStats) || !seasonBrandStats.length) {
      return [];
    }
    const map = new Map();
    seasonBrandStats.forEach((row) => {
      const code = (row.brandCode ?? "").toString();
      if (!code) return;
      const label = row.brandDescription || code;
      map.set(code, label);
    });
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [seasonBrandStats]);

  useEffect(() => {
    if (!brandSeasonOptions.length) {
      setSelectedBrand(null);
    }
  }, [brandSeasonOptions]);

  const seasonCombinedCharts = useMemo(() => {
    const rawTextStats =
      data?.seasonStats ??
      data?.seasonPublishingStats ??
      data?.publishingBySeason ??
      data?.seasonalPublishingStats ??
      [];
    const rawImageStats = data?.seasonImageStats ?? [];

    const textStatsMap = new Map();
    (Array.isArray(rawTextStats)
      ? rawTextStats
      : Object.values(rawTextStats)
    ).forEach((row) => {
      const seasonCode =
        row.season ??
        row.seasonCode ??
        row.stagione ??
        row.code ??
        row.id ??
        row.label;
      if (seasonCode && seasonsOfInterest.includes(seasonCode)) {
        textStatsMap.set(seasonCode, row);
      }
    });

    const imageStatsMap = new Map();
    rawImageStats.forEach((row) => {
      const seasonCode =
        row.season ??
        row.seasonCode ??
        row.stagione ??
        row.code ??
        row.id ??
        row.label;
      if (seasonCode && seasonsOfInterest.includes(seasonCode)) {
        imageStatsMap.set(seasonCode, row);
      }
    });

    const textOrder = ["SI", "DA CONTROLLARE", "PROBLEMA", "NON PUBBLICARE", "NO"];
    const imageOrder = ["SI", "NON SI TROVA", "NO"];

    return seasonsOfInterest
      .map((seasonCode) => {
        const textStat = textStatsMap.get(seasonCode);
        const textData = textStat
          ? Object.entries(textStat.statuses || textStat.counts || {})
              .map(([status, value]) => ({
                label: status,
                value: Number(value),
                color: PUBLISH_STATUS_COLORS[status] || "#ccc",
              }))
              .sort((a, b) => {
                const idxA = textOrder.indexOf(a.label);
                const idxB = textOrder.indexOf(b.label);
                return (idxA === -1 ? 99 : idxA) - (idxB === -1 ? 99 : idxB);
              })
          : [];

        const imageStat = imageStatsMap.get(seasonCode);
        const imageData = imageStat
          ? Object.entries(imageStat.counts || {})
              .map(([status, value]) => ({
                label: status,
                value: Number(value),
                color: IMAGE_STATUS_COLORS[status] || "#ccc",
              }))
              .sort((a, b) => {
                const idxA = imageOrder.indexOf(a.label);
                const idxB = imageOrder.indexOf(b.label);
                return (idxA === -1 ? 99 : idxA) - (idxB === -1 ? 99 : idxB);
              })
          : [];

        const textTotal = textData.reduce((acc, curr) => acc + curr.value, 0);
        const imageTotal = imageData.reduce((acc, curr) => acc + curr.value, 0);

        return {
          seasonCode,
          textData,
          imageData,
          total: Math.max(textTotal, imageTotal),
        };
      })
      .filter((chart) => chart.total > 0);
  }, [data, seasonsOfInterest]);

  const seasonBrandCombinedCharts = useMemo(() => {
    if (!data?.seasonBrandStats || !selectedBrand) return []; // Check selectedBrand

    // Filter text stats for selected brand
    const textStats = data.seasonBrandStats.filter(
      (s) =>
        s.brandCode === selectedBrand.value &&
        seasonsOfInterest.includes(s.season)
    );

    // Filter image stats for selected brand
    const imageStats = data.seasonBrandImageStats
      ? data.seasonBrandImageStats.filter(
          (s) =>
            s.brandCode === selectedBrand.value &&
            seasonsOfInterest.includes(s.season)
        )
      : [];

    // Map image stats by season for quick lookup
    const imageStatsMap = new Map();
    imageStats.forEach((s) => imageStatsMap.set(s.season, s));

    const textOrder = ["SI", "DA CONTROLLARE", "PROBLEMA", "NON PUBBLICARE", "NO"];
    const imageOrder = ["SI", "NON SI TROVA", "NO"];

    return seasonsOfInterest
      .map((seasonCode) => {
        const textStat = textStats.find((s) => s.season === seasonCode);
        const textData = textStat
          ? Object.entries(textStat.counts || {})
              .map(([status, value]) => ({
                label: status,
                value: Number(value),
                color: PUBLISH_STATUS_COLORS[status] || "#ccc",
              }))
              .sort((a, b) => {
                const idxA = textOrder.indexOf(a.label);
                const idxB = textOrder.indexOf(b.label);
                return (idxA === -1 ? 99 : idxA) - (idxB === -1 ? 99 : idxB);
              })
          : [];

        const imageStat = imageStatsMap.get(seasonCode);
        const imageData = imageStat
          ? Object.entries(imageStat.counts || {})
              .map(([status, value]) => ({
                label: status,
                value: Number(value),
                color: IMAGE_STATUS_COLORS[status] || "#ccc",
              }))
              .sort((a, b) => {
                const idxA = imageOrder.indexOf(a.label);
                const idxB = imageOrder.indexOf(b.label);
                return (idxA === -1 ? 99 : idxA) - (idxB === -1 ? 99 : idxB);
              })
          : [];

        const textTotal = textData.reduce((acc, curr) => acc + curr.value, 0);
        const imageTotal = imageData.reduce((acc, curr) => acc + curr.value, 0);

        return {
          seasonCode,
          textData,
          imageData,
          total: Math.max(textTotal, imageTotal),
        };
      })
      .filter((chart) => chart.total > 0);
  }, [data, seasonsOfInterest, selectedBrand]);

  const displayedCharts = useMemo(() => {
    if (selectedBrand) {
      return seasonBrandCombinedCharts;
    }
    return seasonCombinedCharts;
  }, [selectedBrand, seasonBrandCombinedCharts, seasonCombinedCharts]);

  const users = data?.users ?? [];
  const userPublishedStats = data?.userPublishedStats ?? [];
  const weeklySeries = data?.weeklySeries ?? [];

  const userDisplayNameMap = useMemo(() => {
    const map = new Map();
    users.forEach((user) => {
      if (user?.userCode) {
        map.set(user.userCode, user.displayName || user.userCode);
      }
    });
    return map;
  }, [users]);

  const weeklyLineChart = useMemo(() => {
    if (!Array.isArray(weeklySeries) || !weeklySeries.length) {
      return { dataset: [], series: [] };
    }
    const sorted = [...weeklySeries].sort((a, b) => {
      const aDate = parseWeekStart(a)?.getTime() ?? 0;
      const bDate = parseWeekStart(b)?.getTime() ?? 0;
      return aDate - bDate;
    });
    const weekMap = new Map(
      sorted.map((row) => [
        formatWeekLabel(row),
        {
          totalArticoli: Number(row.totalArticoli ?? row.total_articoli ?? NaN),
          withStock: Number(row.withStock ?? row.with_stock ?? NaN),
          publishedTotal: Number(
            row.publishedTotal ?? row.published_total ?? NaN
          ),
          publishedWithStock: Number(
            row.publishedWithStock ?? row.published_with_stock ?? NaN
          ),
          imagesTotal: Number(row.imagesTotal ?? row.images_total ?? NaN),
          imagesWithStock: Number(
            row.imagesWithStock ?? row.images_with_stock ?? NaN
          ),
          weekStart: parseWeekStart(row),
        },
      ])
    );

    const firstDate = parseWeekStart(sorted[0]);
    const lastDate = parseWeekStart(sorted[sorted.length - 1]);
    if (!firstDate || !lastDate) {
      return { dataset: [], series: [] };
    }
    const dataset = [];
    const normalizeValue = (value) => {
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    };
    const MS_WEEK = 7 * 24 * 60 * 60 * 1000;
    const now = new Date();
    const { year: currentYear, isoWeek: currentWeek } = getIsoWeekInfo(now);

    for (
      let ts = firstDate.getTime();
      ts <= lastDate.getTime();
      ts += MS_WEEK
    ) {
      const currentDate = new Date(ts);
      const { year, isoWeek } = getIsoWeekInfo(currentDate);

      // Exclude current week and future weeks
      if (
        year > currentYear ||
        (year === currentYear && isoWeek >= currentWeek)
      ) {
        continue;
      }

      const weekLabel = formatWeekLabel({ year, isoWeek });
      const entry = weekMap.get(weekLabel);
      dataset.push({
        label: weekLabel,
        totalArticoli: entry ? normalizeValue(entry.totalArticoli) : null,
        withStock: entry ? normalizeValue(entry.withStock) : null,
        publishedTotal: entry ? normalizeValue(entry.publishedTotal) : null,
        publishedWithStock: entry
          ? normalizeValue(entry.publishedWithStock)
          : null,
        imagesTotal: entry ? normalizeValue(entry.imagesTotal) : null,
        imagesWithStock: entry ? normalizeValue(entry.imagesWithStock) : null,
      });
    }
    const hasData = dataset.some((row) =>
      [
        "totalArticoli",
        "withStock",
        "publishedTotal",
        "publishedWithStock",
        "imagesTotal",
        "imagesWithStock",
      ].some((key) => Number.isFinite(row[key]))
    );
    if (!hasData) {
      return { dataset: [], series: [] };
    }
    return {
      dataset,
      series: [
        {
          type: "line",
          dataKey: "totalArticoli",
          label: "Articoli totali",
          color: "#78909c",
          valueFormatter: formatValueTooltip,
          connectNulls: false,
        },
        {
          type: "line",
          dataKey: "withStock",
          label: "Con stock",
          color: DASHBOARD_STOCK_COLOR,
          valueFormatter: formatValueTooltip,
          connectNulls: false,
        },
        {
          type: "line",
          dataKey: "publishedTotal",
          label: "Pubblicati tot.",
          color: DASHBOARD_TEXT_COLOR,
          valueFormatter: formatValueTooltip,
          connectNulls: false,
        },
        {
          type: "line",
          dataKey: "publishedWithStock",
          label: "Pubbl. con stock",
          color: lightenColor(DASHBOARD_TEXT_COLOR, 0.35),
          valueFormatter: formatValueTooltip,
          connectNulls: false,
        },
        {
          type: "line",
          dataKey: "imagesTotal",
          label: "Immagini tot.",
          color: DASHBOARD_IMAGE_COLOR,
          valueFormatter: formatValueTooltip,
          connectNulls: false,
        },
        {
          type: "line",
          dataKey: "imagesWithStock",
          label: "Immagini con stock",
          color: lightenColor(DASHBOARD_IMAGE_COLOR, 0.35),
          valueFormatter: formatValueTooltip,
          connectNulls: false,
        },
      ],
    };
  }, [weeklySeries]);

  const publishedChart = useMemo(() => {
    const brandLabels = new Map();
    const brandTotals = new Map();
    const byUser = new Map();
    userPublishedStats.forEach((row) => {
      const key = row?.userCode;
      if (!key) {
        return;
      }
      const normalizedKey = key.toLowerCase();
      const entry = byUser.get(normalizedKey) ?? {
        userCode: key,
        displayName: row?.displayName || userDisplayNameMap.get(key) || key,
        brands: new Map(),
      };
      byUser.set(normalizedKey, entry);
      const textsValue = Number(row?.texts ?? 0);
      const importedValue = Math.min(
        Math.max(Number(row?.textsImported ?? 0), 0),
        textsValue
      );
      const freshTexts = Math.max(textsValue - importedValue, 0);
      const imagesValue = Number(row?.images ?? 0);
      if (freshTexts <= 0 && importedValue <= 0 && imagesValue <= 0) {
        return;
      }
      const brandKey = normalizeBrandKey(row?.brand);
      const label = getBrandLabel(row?.brand, row?.brandDescription);
      brandLabels.set(brandKey, label);
      const stats = entry.brands.get(brandKey) ?? {
        textsFresh: 0,
        textsImported: 0,
        images: 0,
      };
      stats.textsFresh = (stats.textsFresh ?? 0) + freshTexts;
      stats.textsImported = (stats.textsImported ?? 0) + importedValue;
      stats.images = (stats.images ?? 0) + imagesValue;
      entry.brands.set(brandKey, stats);
      const totals = brandTotals.get(brandKey) ?? {
        textsFresh: 0,
        textsImported: 0,
        images: 0,
      };
      totals.textsFresh = (totals.textsFresh ?? 0) + freshTexts;
      totals.textsImported = (totals.textsImported ?? 0) + importedValue;
      totals.images = (totals.images ?? 0) + imagesValue;
      brandTotals.set(brandKey, totals);
    });
    const brandKeys = Array.from(brandLabels.keys());
    if (!brandKeys.length || byUser.size === 0) {
      return { dataset: [], series: [] };
    }
    const dataset = Array.from(byUser.values()).map((entry) => {
      const row = {
        userCode: entry.userCode,
        displayName: entry.displayName,
      };
      brandKeys.forEach((brandKey) => {
        const stats = entry.brands.get(brandKey) ?? {
          textsFresh: 0,
          textsImported: 0,
          images: 0,
        };
        row[`pub_texts_new_${brandKey}`] = stats.textsFresh ?? 0;
        row[`pub_texts_import_${brandKey}`] = stats.textsImported ?? 0;
        row[`pub_images_${brandKey}`] = stats.images ?? 0;
      });
      return row;
    });
    const series = [];
    brandKeys.forEach((brandKey) => {
      const label = brandLabels.get(brandKey);
      const totals = brandTotals.get(brandKey) ?? {
        textsFresh: 0,
        textsImported: 0,
        images: 0,
      };
      const baseColor = computeBrandColor(brandKey);
      const importColor = lightenColor(baseColor, 0.25);
      const imageColor = lightenColor(baseColor, 0.5);
      if (totals.textsFresh > 0) {
        series.push({
          type: "bar",
          dataKey: `pub_texts_new_${brandKey}`,
          label: `${label} · Testi`,
          stack: "published_texts",
          color: baseColor,
        });
      }
      if (totals.textsImported > 0) {
        series.push({
          type: "bar",
          dataKey: `pub_texts_import_${brandKey}`,
          label: `${label} · Import`,
          stack: "published_texts",
          color: importColor,
        });
      }
      if (totals.images > 0) {
        series.push({
          type: "bar",
          dataKey: `pub_images_${brandKey}`,
          label: `${label} · Immagini`,
          stack: "published_images",
          color: imageColor,
        });
      }
    });
    return { dataset, series };
  }, [userPublishedStats, userDisplayNameMap]);

  const assignedChart = useMemo(() => {
    const brandLabels = new Map();
    users.forEach((user) => {
      (user.brandsPendingTexts || []).forEach((brand) => {
        const brandKey = normalizeBrandKey(
          brand.brandCode || brand.brand || brand.brand_code
        );
        const value = Number(brand.count ?? brand.total ?? 0);
        const importedValue = Math.min(
          Math.max(Number(brand.importedCount ?? 0), 0),
          value
        );
        if (value <= 0 && importedValue <= 0) {
          return;
        }
        const label = getBrandLabel(
          brand.brandCode || brand.brand || brand.brand_code,
          brand.brandDescription || brand.brand_description
        );
        brandLabels.set(brandKey, label);
      });
      (user.brandsPendingImages || []).forEach((brand) => {
        const brandKey = normalizeBrandKey(
          brand.brandCode || brand.brand || brand.brand_code
        );
        const value = Number(brand.count ?? brand.total ?? 0);
        if (value <= 0) {
          return;
        }
        const label = getBrandLabel(
          brand.brandCode || brand.brand || brand.brand_code,
          brand.brandDescription || brand.brand_description
        );
        brandLabels.set(brandKey, label);
      });
    });
    const brandKeys = Array.from(brandLabels.keys());
    if (!brandKeys.length) {
      return { dataset: [], series: [] };
    }
    const brandTotals = new Map();
    const dataset = users.map((user) => {
      const displayName = user.displayName || user.userCode;
      const row = {
        userCode: user.userCode,
        displayName,
      };
      const textsMap = new Map();
      const textsImportMap = new Map();
      (user.brandsPendingTexts || []).forEach((brand) => {
        const brandKey = normalizeBrandKey(
          brand.brandCode || brand.brand || brand.brand_code
        );
        const value = Number(brand.count ?? brand.total ?? 0);
        const importedValue = Math.min(
          Math.max(Number(brand.importedCount ?? 0), 0),
          value
        );
        const freshValue = Math.max(value - importedValue, 0);
        textsMap.set(brandKey, (textsMap.get(brandKey) ?? 0) + freshValue);
        textsImportMap.set(
          brandKey,
          (textsImportMap.get(brandKey) ?? 0) + importedValue
        );
        const totals = brandTotals.get(brandKey) ?? {
          textsFresh: 0,
          textsImported: 0,
          images: 0,
        };
        totals.textsFresh = (totals.textsFresh ?? 0) + freshValue;
        totals.textsImported = (totals.textsImported ?? 0) + importedValue;
        brandTotals.set(brandKey, totals);
      });
      const imagesMap = new Map();
      (user.brandsPendingImages || []).forEach((brand) => {
        const brandKey = normalizeBrandKey(
          brand.brandCode || brand.brand || brand.brand_code
        );
        const value = Number(brand.count ?? brand.total ?? 0);
        imagesMap.set(brandKey, value);
        const totals = brandTotals.get(brandKey) ?? {
          textsFresh: 0,
          textsImported: 0,
          images: 0,
        };
        totals.images = (totals.images ?? 0) + value;
        brandTotals.set(brandKey, totals);
      });
      brandKeys.forEach((brandKey) => {
        row[`assign_texts_${brandKey}`] = textsMap.get(brandKey) ?? 0;
        row[`assign_texts_import_${brandKey}`] =
          textsImportMap.get(brandKey) ?? 0;
        row[`assign_images_${brandKey}`] = imagesMap.get(brandKey) ?? 0;
      });
      return row;
    });
    const filteredDataset = dataset.filter((row) =>
      brandKeys.some(
        (brandKey) =>
          (row[`assign_texts_${brandKey}`] ?? 0) > 0 ||
          (row[`assign_texts_import_${brandKey}`] ?? 0) > 0 ||
          (row[`assign_images_${brandKey}`] ?? 0) > 0
      )
    );
    if (!filteredDataset.length) {
      return { dataset: [], series: [] };
    }
    const series = [];
    brandKeys.forEach((brandKey) => {
      const label = brandLabels.get(brandKey);
      const totals = brandTotals.get(brandKey) ?? {
        textsFresh: 0,
        textsImported: 0,
        images: 0,
      };
      const baseColor = computeBrandColor(brandKey);
      const importColor = lightenColor(baseColor, 0.25);
      const imageColor = lightenColor(baseColor, 0.5);
      if (totals.textsFresh > 0) {
        series.push({
          type: "bar",
          dataKey: `assign_texts_${brandKey}`,
          label: `${label} · Testi`,
          stack: "assigned_texts",
          color: baseColor,
        });
      }
      if (totals.textsImported > 0) {
        series.push({
          type: "bar",
          dataKey: `assign_texts_import_${brandKey}`,
          label: `${label} · Import`,
          stack: "assigned_texts",
          color: importColor,
        });
      }
      if (totals.images > 0) {
        series.push({
          type: "bar",
          dataKey: `assign_images_${brandKey}`,
          label: `${label} · Immagini`,
          stack: "assigned_images",
          color: imageColor,
        });
      }
    });
    return {
      dataset: filteredDataset,
      series,
    };
  }, [users]);

  const publishedTotalsChart = useMemo(() => {
    if (!users.length) {
      return { dataset: [], series: [] };
    }
    const dataset = users.map((user) => {
      const { fresh, imported } = splitImportedValues(
        user.publishedInRange,
        user.publishedTextsImported
      );
      return {
        userCode: user.userCode,
        displayName: user.displayName || user.userCode,
        textsNew: fresh,
        textsImported: imported,
        images: Number(user.imagesInRange ?? 0),
      };
    });
    const filtered = dataset.filter(
      (row) =>
        Number(row.textsNew) > 0 ||
        Number(row.textsImported) > 0 ||
        Number(row.images) > 0
    );
    if (!filtered.length) {
      return { dataset: [], series: [] };
    }
    const importColor = lightenColor(DASHBOARD_TEXT_COLOR, 0.4);
    return {
      dataset: filtered,
      series: [
        {
          type: "bar",
          dataKey: "textsNew",
          label: "Nuovi testi",
          stack: "published_total",
          color: DASHBOARD_TEXT_COLOR,
        },
        {
          type: "bar",
          dataKey: "textsImported",
          label: "Controllo testi",
          stack: "published_total",
          color: importColor,
        },
        {
          type: "bar",
          dataKey: "images",
          label: "Immagini",
          stack: "published_total",
          color: DASHBOARD_IMAGE_COLOR,
        },
      ],
    };
  }, [users]);

  const assignedTotalsChart = useMemo(() => {
    if (!users.length) {
      return { dataset: [], series: [] };
    }
    const dataset = users.map((user) => {
      const { fresh, imported } = splitImportedValues(
        user.pendingTexts,
        user.pendingTextsImported
      );
      return {
        userCode: user.userCode,
        displayName: user.displayName || user.userCode,
        textsNew: fresh,
        textsImported: imported,
        images: Number(user.pendingImages ?? 0),
      };
    });
    const filtered = dataset.filter(
      (row) =>
        Number(row.textsNew) > 0 ||
        Number(row.textsImported) > 0 ||
        Number(row.images) > 0
    );
    if (!filtered.length) {
      return { dataset: [], series: [] };
    }
    const importColor = lightenColor(DASHBOARD_TEXT_COLOR, 0.4);
    return {
      dataset: filtered,
      series: [
        {
          type: "bar",
          dataKey: "textsNew",
          label: "Nuovi testi",
          stack: "assigned_total",
          color: DASHBOARD_TEXT_COLOR,
        },
        {
          type: "bar",
          dataKey: "textsImported",
          label: "Controllo testi",
          stack: "assigned_total",
          color: importColor,
        },
        {
          type: "bar",
          dataKey: "images",
          label: "Immagini",
          stack: "assigned_total",
          color: DASHBOARD_IMAGE_COLOR,
        },
      ],
    };
  }, [users]);

  const publishedDisplayChart = showBrandStacks
    ? publishedChart
    : publishedTotalsChart;
  const assignedDisplayChart = showBrandStacks
    ? assignedChart
    : assignedTotalsChart;

  const teamColumns = useMemo(
    () => [
      {
        name: "Utente",
        width: "170px",
        cell: (row) => <div className="b">{row.displayName}</div>,
      },
      {
        name: "Pubblicati",
        compact: true,
        width: "90px",
        cell: (row) =>
          formatCountWithImport(
            row.publishedInRange,
            row.publishedTextsImported
          ),
      },
      {
        name: "Imm. fatte",
        compact: true,
        width: "90px",
        cell: (row) => formatNumber(row.imagesInRange),
      },
      {
        name: "Testi da fare",
        compact: true,
        width: "90px",
        cell: (row) =>
          formatCountWithImport(row.pendingTexts, row.pendingTextsImported),
      },
      {
        name: "Imma. da fare",
        compact: true,
        width: "90px",
        cell: (row) => formatNumber(row.pendingImages),
      },
      {
        name: "Brand fatti",
        grow: 1,
        cell: (row) => (
          <div className="br2 pa2">
            <BrandList items={row.brandsPublished} />
          </div>
        ),
      },
      {
        name: "Brand da fare (testi)",
        grow: 1,
        cell: (row) => <BrandList items={row.brandsPendingTexts} />,
      },
      {
        name: "Brand da fare (immagini)",
        grow: 1,
        cell: (row) => (
          <div className="br2 pa2">
            <BrandList items={row.brandsPendingImages} />
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div className={`${temaColori} pa3`}>
      <div className="flex flex-wrap items-center justify-between mb3">
        <div>
          <h2 className="ma0">Dashboard pubblicazioni</h2>
        </div>
        <div className="flex items-center">
          {rangeInfo && (
            <div className="mr3">
              Dati dal <span className="b">{rangeInfo.start}</span> al{" "}
              <span className="b">{rangeInfo.end}</span> ({rangeInfo.days}{" "}
              giorni, ora italiana)
            </div>
          )}
          <label className="mr2 b" htmlFor="dashboard-pubbl-days">
            Intervallo
          </label>
          <select
            id="dashboard-pubbl-days"
            className="input-reset br2 ba b--light-gray pa2 mr2"
            value={days}
            onChange={(evt) => setDays(parseInt(evt.target.value, 10))}
          >
            {DAYS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            className={stilePulsanti.buttonClassesWhite}
            onClick={() => setRefreshIndex((value) => value + 1)}
            disabled={loading || !token}
          >
            Aggiorna
          </button>
        </div>
      </div>

      {!token && (
        <div className="bg-washed-yellow dark-gray pa3 br2 mb3">
          Token Auth0 non disponibile: attendi qualche secondo e riprova.
        </div>
      )}

      {error && (
        <div className="bg-washed-red dark-red pa3 br2 mb3">{error}</div>
      )}

      {loading && <div className="pa3 mb3">Caricamento dati…</div>}

      <div className="sfondoBiancoTema br3 pa3 shadow-1 mb3">
        <div className="flex items-center justify-between mb2">
          <h3 className="ma0">Team pubblicazione</h3>
          {updatedAt && (
            <div className="gray f6">
              Aggiornato alle {formatItalianDate(updatedAt)}
            </div>
          )}
        </div>
        <DataTableBase
          columns={teamColumns}
          data={users}
          pagination={false}
          highlightOnHover
          dense
          striped
          noDataComponent={
            <span className="gray">
              Nessun utente configurato con il flag <code>team_pubbl</code>.
            </span>
          }
        />
      </div>

      <div className="statistichePubblicazioni flex flex-wrap mt2">
        {/* <div className="w-100 w-third-ns pr2">
          {summaryCards.length > 0 && (
            <div className="flex flex-wrap mb2">
              {summaryCards.map((card, i) => (
                <div
                  key={card.label}
                  className={
                    i === 0
                      ? "sfondoBiancoTema br3 pa3 ma2 ml0 shadow-1 flex-auto"
                      : "sfondoBiancoTema br3 pa3 ma2 shadow-1 flex-auto"
                  }
                  style={{ minWidth: "180px" }}
                >
                  <div className="f6 ttu gray">{card.label}</div>
                  <div className="f3 b mt2">{formatNumber(card.value)}</div>
                </div>
              ))}
            </div>
          )}
        </div> */}
        <div className="w-100 flex flex-wrap">
          <div className="w-100 w-50-ns pr0 pr2-ns">
            {publishedDisplayChart.series.length > 0 &&
              publishedDisplayChart.dataset.length > 0 && (
                <div className="sfondoBiancoTema br3 pa3 shadow-1 mb2">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="ma0">
                        Pubblicazioni per utente
                        {showBrandStacks ? " e brand" : ""}
                      </h3>
                      {rangeInfo && (
                        <span className="gray f6">
                          Intervallo {rangeInfo.start} → {rangeInfo.end}
                        </span>
                      )}
                    </div>
                    <label className="flex items-center f6 gray">
                      <input
                        type="checkbox"
                        className="mr2"
                        checked={showBrandStacks}
                        onChange={(event) =>
                          setShowBrandStacks(event.target.checked)
                        }
                      />
                      Suddividi per brand
                    </label>
                  </div>
                  {!showBrandStacks && (
                    <div className="gray f7 mb2">
                      Totale testi/immagini completati
                    </div>
                  )}
                  <div className="flex items-center">
                    <div
                      className="gray f7 mr2 flex items-center justify-center"
                      style={{
                        writingMode: "vertical-rl",
                        transform: "rotate(180deg)",
                        height: `${userChartHeight}px`,
                      }}
                    >
                      Articoli
                    </div>
                    <div className="overflow-auto flex-auto">
                      <BarChart
                        height={userChartHeight}
                        dataset={publishedDisplayChart.dataset}
                        series={publishedDisplayChart.series}
                        xAxis={[
                          {
                            dataKey: "displayName",
                            scaleType: "band",
                            tickLabelStyle: {
                              angle:
                                publishedDisplayChart.dataset.length > 5
                                  ? -35
                                  : 0,
                              textAnchor:
                                publishedDisplayChart.dataset.length > 5
                                  ? "end"
                                  : "middle",
                            },
                          },
                        ]}
                        yAxis={[{}]}
                        margin={{ top: 20, right: 40, bottom: 60, left: 60 }}
                        slotProps={{
                          legend: {
                            direction: "column",
                            position: { vertical: "top", horizontal: "right" },
                          },
                        }}
                        tooltip={{ trigger: "item" }}
                      />
                    </div>
                  </div>
                </div>
              )}
          </div>
          <div className="w-100 w-50-ns pl0 pl2-ns">
            {assignedDisplayChart.series.length > 0 &&
              assignedDisplayChart.dataset.length > 0 && (
                <div className="sfondoBiancoTema br3 pa3 shadow-1 mb3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="ma0">
                        Carico assegnato per utente
                        {showBrandStacks ? " e brand" : ""}
                      </h3>
                      <span className="gray f6">
                        Include gli articoli attualmente prenotati
                      </span>
                    </div>
                    <label className="flex items-center f6 gray">
                      <input
                        type="checkbox"
                        className="mr2"
                        checked={showBrandStacks}
                        onChange={(event) =>
                          setShowBrandStacks(event.target.checked)
                        }
                      />
                      Suddividi per brand
                    </label>
                  </div>
                  {!showBrandStacks && (
                    <div className="gray f7 mb2">
                      Totale testi/immagini assegnati per utente
                    </div>
                  )}
                  <div className="flex items-center">
                    <div
                      className="gray f7 mr2 flex items-center justify-center"
                      style={{
                        writingMode: "vertical-rl",
                        transform: "rotate(180deg)",
                        height: `${userChartHeight}px`,
                      }}
                    >
                      Articoli assegnati
                    </div>
                    <div className="overflow-auto flex-auto">
                      <BarChart
                        height={userChartHeight}
                        dataset={assignedDisplayChart.dataset}
                        series={assignedDisplayChart.series}
                        xAxis={[
                          {
                            dataKey: "displayName",
                            scaleType: "band",
                            tickLabelStyle: {
                              angle:
                                assignedDisplayChart.dataset.length > 5
                                  ? -35
                                  : 0,
                              textAnchor:
                                assignedDisplayChart.dataset.length > 5
                                  ? "end"
                                  : "middle",
                            },
                          },
                        ]}
                        yAxis={[{}]}
                        margin={{ top: 20, right: 40, bottom: 60, left: 60 }}
                        slotProps={{
                          legend: {
                            direction: "column",
                            position: { vertical: "top", horizontal: "right" },
                          },
                        }}
                        tooltip={{ trigger: "item" }}
                      />
                    </div>
                  </div>
                </div>
              )}
          </div>
        </div>

        {weeklyLineChart.series.length > 0 &&
          weeklyLineChart.dataset.length > 0 && (
            <div className="w-100 mt3">
              <div className="sfondoBiancoTema br3 pa3 shadow-1 mb3">
                <div className="flex items-center justify-between mb2">
                  <div>
                    <h3 className="ma0">Andamento settimanale</h3>
                    <div className="gray f7">
                      Settimane ISO (fuso Europe/Rome) - ultimi{" "}
                      {historyYears * 52} snapshot
                      <br />
                      (Dati aggiornati alla settimana precedente)
                    </div>
                  </div>
                  <div className="flex items-center">
                    <span className="mr2 f7">ultimi</span>
                    <input
                      type="number"
                      min={1}
                      max={5}
                      value={historyYears}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setHistoryYears(Math.min(5, Math.max(1, v)));
                      }}
                      className="w3 tc br2 ba b--light-gray pa1 mr2"
                    />
                    <span className="f7">anni</span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-center mb3">
                  {weeklyLineChart.series.map((s) => {
                    const isHidden = hiddenSeries[s.dataKey];
                    return (
                      <button
                        key={s.dataKey}
                        onClick={() =>
                          setHiddenSeries((prev) => ({
                            ...prev,
                            [s.dataKey]: !prev[s.dataKey],
                          }))
                        }
                        className="ma1 pa2 pointer br2 b--none f7"
                        style={{
                          backgroundColor: isHidden ? "#eee" : s.color,
                          color: isHidden ? "#333" : "#fff",
                          border: isHidden ? "1px solid #ccc" : "none",
                        }}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center">
                  <div
                    className="gray f7 mr2 flex items-center justify-center"
                    style={{
                      writingMode: "vertical-rl",
                      transform: "rotate(180deg)",
                      height: "320px",
                    }}
                  >
                    Articoli
                  </div>
                  <div className="overflow-auto flex-auto">
                    <LineChart
                      height={320}
                      dataset={weeklyLineChart.dataset}
                      series={weeklyLineChart.series.filter(
                        (s) => !hiddenSeries[s.dataKey]
                      )}
                      xAxis={[
                        {
                          dataKey: "label",
                          scaleType: "point",
                        },
                      ]}
                      yAxis={[{ label: "Articoli" }]}
                      margin={{ top: 20, right: 40, bottom: 40, left: 60 }}
                      slotProps={{
                        legend: {
                          direction: "column",
                          position: { vertical: "top", horizontal: "right" },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

        <div className="w-100 mt3">
          <div className="w-100">
            <div className="sfondoBiancoTema br3 pa3 shadow-1 mb3">
              <div className="flex items-center justify-between flex-wrap mb3">
                <div className="flex items-center">
                  <h3 className="ma0 mr3">
                    Stato pubblicazione{" "}
                    {selectedBrand ? `- ${selectedBrand.label}` : "Globale"}
                  </h3>
                  {selectedBrand && (
                    <button
                      className="f7 bn bg-transparent gray pointer underline"
                      onClick={() => setSelectedBrand(null)}
                    >
                      Rimuovi filtro
                    </button>
                  )}
                </div>
                <div className="flex flex-column f6 gray mt2 mr3">
                  <span className="mb1">Articoli considerati</span>
                  <div
                    className="flex flex-wrap"
                    role="group"
                    aria-label="Filtro stock"
                  >
                    {STOCK_FILTER_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`${
                          stockMode === option.value
                            ? stilePulsanti.buttonClassesGreen
                            : stilePulsanti.buttonClassesWhite
                        } mr1 mb1 f7`}
                        aria-pressed={stockMode === option.value}
                        onClick={() => setStockMode(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center">
                  <button
                    className={stilePulsanti.buttonClassesWhite + " mr3 f7"}
                    onClick={() => setMinSeasonYear((prev) => prev - 1)}
                  >
                    Carica stagioni precedenti
                  </button>
                  <div style={{ minWidth: 250 }}>
                    <Autocomplete
                      options={brandSeasonOptions}
                      getOptionLabel={(option) => option.label || ""}
                      value={selectedBrand}
                      onChange={(event, newValue) => {
                        setSelectedBrand(newValue);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Filtra Brand"
                          variant="outlined"
                          size="small"
                        />
                      )}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                      }
                    />
                  </div>
                </div>
              </div>

              <div
                className="flex flex-wrap justify-start pa3"
                style={{ gap: "20px" }}
              >
                {loading && (
                  <div className="gray pa3 w-100 tc">Caricamento dati…</div>
                )}
                {!loading &&
                  displayedCharts.map((chart) => (
                    <div
                      key={chart.seasonCode}
                      className="pa2 pt3 pb3 w-100 w-30-l br2 ba b--black-10 mb3 bg-white flex flex-column items-center"
                    >
                      <div className="b f4 mb2">
                        {stockFilterLabel
                          ? `${chart.seasonCode} - ${stockFilterLabel}`
                          : chart.seasonCode}
                      </div>
                      <PieChart
                        series={[
                          {
                            arcLabel: (item) =>
                              item.value > 0 ? String(item.value) : "",
                            data: chart.textData,
                            outerRadius: 70, // Inner circle (Text)
                            cx: 140,
                            cy: 140,
                          },
                          {
                            arcLabel: (item) =>
                              item.value > 0 ? String(item.value) : "", // Simple value label
                            data: chart.imageData,
                            innerRadius: 80, // Outer circle (Images)
                            outerRadius: 110,
                            cx: 140,
                            cy: 140,
                          },
                        ]}
                        width={300}
                        height={300}
                        legend={{ hidden: true }}
                        sx={{
                          [`& .${pieArcLabelClasses.root}`]: {
                            fill: "white",
                            fontWeight: "bold",
                            fontSize: 10,
                          },
                        }}
                      />
                      <div className="mb3 f6 gray">
                        Totale articoli:{" "}
                        <span className="b">{chart.total}</span>
                      </div>
                      <div className="flex w-100 mt2 f7 justify-around pb3">
                        <div className="tl w-50 ph2">
                          <span className="ttu b db">Testi</span>
                          <div className="mb3">( cerchio interno )</div>
                          {chart.textData.map((d) => (
                            <div
                              key={d.label}
                              className="flex items-center mb2"
                            >
                              <span
                                className="w1 h1 dib mr1 br-100"
                                style={{ backgroundColor: d.color }}
                              ></span>
                              {d.label}:{" "}
                              <span className="ml2 b">{d.value}</span>
                            </div>
                          ))}
                        </div>
                        <div className="tl w-50 ph2">
                          <span className="ttu b db mb1">Immagini</span>
                          <div className="mb3">( cerchio esterno )</div>
                          {chart.imageData.map((d) => (
                            <div
                              key={d.label}
                              className="flex items-center mb2"
                            >
                              <span
                                className="w1 h1 dib mr1 br-100"
                                style={{ backgroundColor: d.color }}
                              ></span>
                              {d.label}:{" "}
                              <span className="ml2 b">{d.value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                {!loading && displayedCharts.length === 0 && (
                  <div className="gray pa3 w-100 tc">
                    Nessun dato stagionale{" "}
                    {selectedBrand ? "per il brand selezionato " : ""}nel
                    periodo.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardPubblicazioni;
